import React from "react";
import { motion } from "framer-motion";

/**
 * VoloTag — a signature pebble-shaped object with a notched "V" edge.
 * Deliberately not a generic round disc. The form is the identity.
 */
const TagVisual = ({ size = 340, floating = true, listening = true }) => {
  const Wrapper = floating ? motion.div : "div";
  const floatProps = floating
    ? {
        animate: { y: [0, -10, 0], rotate: [-1.5, 1.5, -1.5] },
        transition: { duration: 7, repeat: Infinity, ease: "easeInOut" },
      }
    : {};

  return (
    <Wrapper
      {...floatProps}
      style={{ width: size, height: size }}
      className="relative flex items-center justify-center"
      data-testid="tag-visual"
    >
      {/* Soft halo */}
      <div className="absolute inset-0 glow-blue rounded-full blur-2xl opacity-70" />
      <div className="absolute inset-10 glow-silver rounded-full blur-xl opacity-60" />

      <svg
        viewBox="0 0 320 320"
        className="relative drop-shadow-[0_40px_70px_rgba(15,23,42,0.22)]"
        width={size * 0.86}
        height={size * 0.86}
      >
        <defs>
          <linearGradient id="vt-body" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="45%" stopColor="#F4F4F5" />
            <stop offset="100%" stopColor="#C8C8CD" />
          </linearGradient>
          <linearGradient id="vt-edge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.1)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
          </linearGradient>
          <radialGradient id="vt-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#BFDBFE" />
            <stop offset="50%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#1D4ED8" />
          </radialGradient>
          <linearGradient id="vt-shine" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.85)" />
            <stop offset="45%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {/* Signature squircle/pebble body with slight asymmetry */}
        <path
          d="M160 30 C 230 30, 290 70, 290 160 C 290 250, 230 290, 160 290 C 90 290, 30 250, 30 160 C 30 70, 90 30, 160 30 Z"
          fill="url(#vt-body)"
        />
        <path
          d="M160 30 C 230 30, 290 70, 290 160 C 290 250, 230 290, 160 290 C 90 290, 30 250, 30 160 C 30 70, 90 30, 160 30 Z"
          fill="none"
          stroke="url(#vt-edge)"
          strokeWidth="1.2"
        />

        {/* Signature V-notch on the top edge — the VoloTag identity */}
        <path
          d="M136 28 L160 54 L184 28"
          stroke="#0A0A0A"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.75"
        />

        {/* Inner recessed panel */}
        <path
          d="M160 72 C 210 72, 248 108, 248 160 C 248 212, 210 248, 160 248 C 110 248, 72 212, 72 160 C 72 108, 110 72, 160 72 Z"
          fill="#FAFAFA"
          stroke="#E4E4E7"
          strokeWidth="0.8"
        />

        {/* Ambient dotted ring */}
        <circle cx="160" cy="160" r="72" fill="none" stroke="#3B82F6" strokeWidth="0.7" strokeDasharray="2 5" opacity="0.45" />

        {/* Core — the "voice" */}
        <motion.circle
          cx="160"
          cy="160"
          r="38"
          fill="url(#vt-core)"
          initial={{ r: 38, opacity: 1 }}
          animate={listening ? { r: [36, 42, 36], opacity: [0.95, 1, 0.95] } : { r: 38, opacity: 1 }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        />
        <circle cx="160" cy="160" r="38" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.6" />

        {/* Equaliser inside core */}
        {listening && (
          <g transform="translate(160,160)">
            {[-12, -4, 4, 12].map((x, i) => (
              <motion.rect
                key={x}
                x={x - 1.5}
                y={-6}
                width="3"
                height="12"
                rx="1.5"
                fill="rgba(255,255,255,0.92)"
                initial={{ height: 12, y: -6 }}
                animate={{ height: [6, 15, 6], y: [-3, -7.5, -3] }}
                transition={{ duration: 0.95 + i * 0.1, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }}
              />
            ))}
          </g>
        )}

        {/* Top-left gloss highlight (premium finish) */}
        <path
          d="M60 80 C 100 50, 140 45, 180 55"
          stroke="url(#vt-shine)"
          strokeWidth="14"
          strokeLinecap="round"
          fill="none"
          opacity="0.85"
        />
      </svg>

      {/* Sound waves */}
      {listening && (
        <>
          <motion.span
            className="absolute w-28 h-28 rounded-full border border-blue-400/45"
            animate={{ scale: [1, 3], opacity: [0.75, 0] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut" }}
          />
          <motion.span
            className="absolute w-28 h-28 rounded-full border border-blue-400/30"
            animate={{ scale: [1, 3], opacity: [0.75, 0] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut", delay: 0.95 }}
          />
          <motion.span
            className="absolute w-28 h-28 rounded-full border border-blue-400/20"
            animate={{ scale: [1, 3], opacity: [0.75, 0] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut", delay: 1.9 }}
          />
        </>
      )}
    </Wrapper>
  );
};

export default TagVisual;
